import { statesByCountry } from "@wayflyer/flyui";
import { CompanyCreationSchemas } from "api-types";
import { useIntl } from "react-intl";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useAxios } from "auth-sdk";
import type { AxiosError, AxiosResponse } from "axios";

import type { StateRegion, SupportedCountry } from "../countries/types";

export const undefinedCompanyIdString = "__user_added_company__";

export type CompanySearchFormFields = {
  company: string;
  company_id?: string;
  region?: string;
  country: string;
};

type CompanySearch = {
  searchTerm: string;
  country: SupportedCountry;
  state?: string;
};

export const OPEN_CORPORATES_COMPANY_SEARCH_KEY = "open-corporates-company-search";

export const useStateRegion = (country: SupportedCountry): StateRegion => {
  const intl = useIntl();
  const lookup: Record<SupportedCountry, StateRegion> = {
    US: {
      label: intl.formatMessage({ id: "shared-utils: State", defaultMessage: "State" }),
      options: statesByCountry["US"],
      requiredBySearch: true,
      searchOptionsLabel: intl.formatMessage({
        id: "shared-utils: US State of Registration",
        defaultMessage: "US State of Registration",
      }),
    },
    CA: {
      label: intl.formatMessage({ id: "shared-utils: Province", defaultMessage: "Province" }),
      options: statesByCountry["CA"],
      requiredBySearch: true,
      searchOptions: [
        { id: "AB", text: "Alberta" },
        { id: "BC", text: "British Columbia" },
        { id: "MB", text: "Manitoba" },
        { id: "NB", text: "New Brunswick" },
        { id: "NL", text: "Newfoundland and Labrador" },
        { id: "NT", text: "Northwest Territories" },
        { id: "NS", text: "Nova Scotia" },
        { id: "NU", text: "Nunavut" },
        { id: "ON", text: "Ontario" },
        { id: "PE", text: "Prince Edward Island" },
        { id: "QC", text: "Québec" },
        { id: "SK", text: "Saskatchewan" },
        { id: "YT", text: "Yukon" },
      ],
      searchOptionsLabel: intl.formatMessage({
        id: "shared-utils: Canadian Province of Incorporation",
        defaultMessage: "Canadian Province of Incorporation",
      }),
    },
    IE: {
      label: intl.formatMessage({
        id: "shared-utils: County",
        defaultMessage: "County",
      }),
      requiredBySearch: false,
    },
    GB: {
      label: intl.formatMessage({
        id: "shared-utils: County",
        defaultMessage: "County",
      }),
      requiredBySearch: false,
    },
    AU: {
      label: intl.formatMessage({
        id: "shared-utils: Territory",
        defaultMessage: "Territory",
      }),
      options: statesByCountry["AU"],
      requiredBySearch: false,
    },
    ES: {
      label: intl.formatMessage({ id: "shared-utils: Province", defaultMessage: "Province" }),
      requiredBySearch: false,
    },
    NL: {
      label: intl.formatMessage({ id: "shared-utils: Province", defaultMessage: "Province" }),
      options: statesByCountry["NL"],
      requiredBySearch: false,
    },
    BE: {
      label: intl.formatMessage({ id: "shared-utils: Province", defaultMessage: "Province" }),
      options: statesByCountry["BE"],
      requiredBySearch: false,
    },
    DK: {
      label: intl.formatMessage({ id: "shared-utils: Province", defaultMessage: "Province" }),
      requiredBySearch: false,
    },
    SE: {
      label: intl.formatMessage({ id: "shared-utils: Region", defaultMessage: "Region" }),
      requiredBySearch: false,
    },
    NZ: {
      label: intl.formatMessage({ id: "shared-utils: Region", defaultMessage: "Region" }),
      requiredBySearch: false,
    },
    DE: {
      label: intl.formatMessage({ id: "shared-utils: State", defaultMessage: "State" }),
      options: statesByCountry["DE"],
      requiredBySearch: false,
    },
    FR: {
      label: intl.formatMessage({ id: "shared-utils: Region", defaultMessage: "Region" }),
      options: statesByCountry["FR"],
      requiredBySearch: false,
    },
  };
  return lookup[country];
};

export const useCompanySearch = ({ searchTerm = "", country, state }: CompanySearch) => {
  const client = useAxios();
  const params: CompanyCreationSchemas.SearchIn = {
    search_str: searchTerm,
    country: country as CompanyCreationSchemas.SearchIn["country"],
    state,
  };
  // If the country is US or CA, we need to ensure that the state is also provided
  const readyForSearch = !!country && !((country === "US" || country === "CA") && !state);
  return useQuery({
    queryKey: [OPEN_CORPORATES_COMPANY_SEARCH_KEY, searchTerm, state],
    queryFn: ({ signal }) =>
      client.post<CompanyCreationSchemas.SearchOut>("/company-creation/api/open-corporates/search", params, {
        signal,
        timeout: 4500,
      }),
    select: ({ data }: AxiosResponse<CompanyCreationSchemas.SearchOut>) => data,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    retry: (failureCount: number, error: AxiosError) => {
      // Don't retry for 404 and 400 errors
      if (error.response?.status === 404 || error.response?.status === 400) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => {
      // Exponential backoff: 1s, 3s, 9s -> max 10s
      return Math.min(1000 * 1 * 3 ** attemptIndex, 10000);
    },
    enabled: searchTerm.length > 2 && readyForSearch,
  });
};
